import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import * as FormDialog from "../shared/FormDialog";
import { hasErrorCode } from "../api/errors";
import type { OptionCategoryFormValues } from "../api/optionCategories";
import type { OptionCategoryListTextKeys } from "./types";

type OptionCategoryFormDialogProps = {
  mode: "create" | "edit";
  textKeys: OptionCategoryListTextKeys;
  hasDescription: boolean;
  defaultValues: OptionCategoryFormValues;
  onSubmit: (values: OptionCategoryFormValues) => Promise<unknown>;
  onClose: () => void;
};

export const OptionCategoryFormDialog = ({
  mode,
  textKeys,
  hasDescription,
  defaultValues,
  onSubmit,
  onClose,
}: OptionCategoryFormDialogProps) => {
  const { t } = useTranslation();
  const submitMutation = useMutation({ mutationFn: onSubmit });
  const {
    control,
    setFocus,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<OptionCategoryFormValues>({ defaultValues });

  FormDialog.useAutoFocusField(setFocus, "label");

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t(mode === "create" ? textKeys.createTitle : textKeys.editTitle)}</FormDialog.Title>
      <FormDialog.Form
        onSubmit={handleSubmit(values =>
          submitMutation.mutate(values, {
            onSuccess: onClose,
            onError: error =>
              hasErrorCode(error, "OPTION_ALREADY_EXISTS")
                ? setError(
                    "label",
                    {
                      type: "optionAlreadyExists",
                      message: t(textKeys.alreadyExistsError),
                    },
                    { shouldFocus: true }
                  )
                : setError("root.serverError", { type: "fallback" }),
          })
        )}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={Boolean(errors.root?.serverError)} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="label"
              rules={{ required: t("Input.Validation.Required") }}
              label={t(textKeys.nameLabel)}
              placeholder={t(textKeys.namePlaceholder)}
            />
            {hasDescription && (
              <FormDialog.TextField
                control={control}
                name="description"
                label={t("OptionCategoryList.Form.Description")}
                multiline
                rows={4}
                placeholder={t(textKeys.descriptionPlaceholder)}
              />
            )}
          </FormDialog.Fields>
        </FormDialog.Content>
        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={submitMutation.isPending}>
            {t(mode === "create" ? "CreateCategory.Dialog.SubmitButton" : "CreateCategory.Dialog.SaveButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
